import React, { type JSX } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Stack, Breadcrumbs, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Colors from '@brightlayer-ui/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Route } from '../router/routes';
import { Spacer } from '@brightlayer-ui/react-components';
import Close from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { HeaderActions } from './HeaderActions';
import { useNavigationBack } from 'hooks';
import { CustomTransComponent } from './';
import { setDashboardWidgetIds } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { setCancelConfirmModalOpen } from '@fiji/common/src/features/entitlements/entitlementSlice';
import { setShowDiscardModal } from '@fiji/common/src/features/emergencyContact/emergencyContactSlice';
import { selectLocale } from '@fiji/common/src/features/localization/localizaitonSlice';

// ---------------------------------------------------------------------------
// Module-level helpers (closure-free) to keep component CCN low
// ---------------------------------------------------------------------------

const computeNoOrganization = (currentOrg: any, location: any, currentRealmName: string): boolean =>
    (!currentOrg?.id && location.pathname === `/${currentRealmName}/organization`) ||
    location.pathname.indexOf('orgmanagementdetails') !== -1;

const getBackgroundSx = (theme: any, pathname: string | undefined): any => ({
    backgroundColor: theme.palette.background.paper,
    ...(pathname?.includes?.('multiDevices') && { zIndex: 1 }),
});

const resolveNoShadowedHeader = (pageHeaderData: any, extraProps: any): any =>
    pageHeaderData?.noShadowedHeader !== undefined ? pageHeaderData?.noShadowedHeader : extraProps?.noShadowedHeader;

const buildShadowedAppBarSx = (): any => ({
    boxShadow: 'none',
    color: 'inherit',
    pr: `0px !important`,
    zIndex: 1299,
});

const buildColoredAppBarSx = (
    timelineStatusColor: string | undefined,
    headerColorChange: string,
    getColors: (color: string) => any
): any => ({
    color: 'inherit',
    backgroundColor: `${timelineStatusColor || getColors(headerColorChange)[500]}`,
    pr: `0px !important`,
    zIndex: 1299,
});

const getAppBarSx = (
    pageHeaderData: any,
    extraProps: any,
    timelineStatusColor: string | undefined,
    headerColorChange: string,
    getColors: (color: string) => any
): any =>
    resolveNoShadowedHeader(pageHeaderData, extraProps)
        ? buildShadowedAppBarSx()
        : buildColoredAppBarSx(timelineStatusColor, headerColorChange, getColors);

const resolveNoBreadcrumbsClassName = (
    currentOrg: any,
    location: any,
    currentRealmName: string,
    isSuperRealm: boolean
): string => {
    const isSecondary = currentOrg?.id || location.pathname === `/${currentRealmName}/myProfile` || isSuperRealm;
    return isSecondary ? 'text-secondary' : 'text-white';
};

const resolveFontColor = (extraProps: any): any => extraProps?.fontColor ?? Colors.black[500];

const resolveHeaderActionsDisabled = (currentOrg: any): boolean => !currentOrg?.id;

// handleNavigateBack sub-helpers ---------------------------------------------

const shouldTriggerCancelConfirm = (pathname: string, flag: boolean): boolean =>
    pathname.includes('addDeviceLicense') && flag;

const shouldTriggerDiscardModal = (pathname: string | undefined, hasUnsaved: boolean): boolean => {
    if (!hasUnsaved) return false;
    return Boolean(pathname?.includes('/editemergencycontact') || pathname?.includes('/addemergencycontacts'));
};

const isOrganizationBackPath = (pathname: string | undefined): boolean => Boolean(pathname?.includes('/organization'));

const resolveEditNavigationSteps = (pathname: string | undefined, stack: number): number => {
    if (pathname?.includes('/editWidget')) return 1;
    if (pathname?.includes('/editDashboard') && stack > 1) return stack;
    if (stack > 1) return stack;
    return 0;
};

// getSubtitle sub-helper -----------------------------------------------------

const resolveSubtitleContent = (extraProps: any, appHeaderSubTitle: any, currentOrgName: any): any => {
    if (extraProps?.noSubtitle) return '';
    const subTitleKey = extraProps?.subTitle;
    if (subTitleKey && !appHeaderSubTitle) {
        return <CustomTransComponent translationKey={subTitleKey} />;
    }
    return appHeaderSubTitle || (currentOrgName ?? '');
};

/* The `InternalPageHeader` component is a React functional component that renders a header for an
internal page. It takes in three props: `nestedRoutesList`, `title`, and `page`. */
export const InternalPageHeader = (props: {
    nestedRoutesList: Route[];
    title: string;
    page: JSX.Element;
    extraProps: Route;
    isServiceAdmin?: boolean;
}): JSX.Element => {
    const { nestedRoutesList, title, page, extraProps } = props;
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { widgetId } = useParams();
    const navigationBack = useNavigationBack();
    const dispatch = useAppDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const isSuperRealm = useTypedSelector((state: RootState) => state.common.isSuperRealm);
    const isAdminRealm = useTypedSelector((state: RootState) => state.common.isAdminRealm);
    const headerColorChange = useSelector((state: any) => state.common.headerColor);
    const timelineStatusColor = useSelector((state: any) => state.common.timelineStatusColor);
    const { t } = useTranslation();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const NO_ORGANIZATION = computeNoOrganization(currentOrg, location, currentRealmName);
    const appHeaderSubTitle = useTypedSelector((state) => state.common.appHeaderSubTitle);
    const pageHeaderData = useTypedSelector((state) => state.common.pageHeader);
    const appHeaderTitle = useTypedSelector((state) => state.common.appHeaderTitle);
    const widgetLoader = useTypedSelector((state) => state.widgetConfiguration.isLoading);
    const widgetNavigatorStack = useTypedSelector((state) => state.widgetConfiguration.saveAsCount);
    const shouldCancelConfirmModalOpen = useTypedSelector((state) => state.entitlement.shouldCancelConfirmModalOpen);
    const hasUnsavedEmergencyContactChanges = useTypedSelector((state) => state.emergencyContacts.hasUnsavedChanges);
    const disableHeaderBackNavigation = useTypedSelector((state) => state.common.disableHeaderBackNavigation);
    const locale = useTypedSelector(selectLocale);

    function redirectionHandler(): void {
        if (location.state?.assignType) {
            dispatch(setDashboardWidgetIds(widgetId));
        }
        handleNavigateBack();
    }

    const handleNavigateBack = (): void => {
        const backNavigationEvent = new CustomEvent('app:header-back', {
            cancelable: true,
            detail: { pathname: location.pathname },
        });

        if (!window.dispatchEvent(backNavigationEvent)) {
            return;
        }

        const locationPathName = window.location.pathname;

        if (shouldTriggerCancelConfirm(location.pathname, shouldCancelConfirmModalOpen)) {
            dispatch(setCancelConfirmModalOpen(true));
            return;
        }
        if (shouldTriggerDiscardModal(locationPathName, hasUnsavedEmergencyContactChanges)) {
            dispatch(setShowDiscardModal(true));
            return;
        }
        if (isOrganizationBackPath(locationPathName)) {
            navigate(`/${currentRealmName}`);
            return;
        }
        // For editWidget pages, only go back 1 step regardless of saveAsCount
        // This ensures: editWidget -> editDashboard -> summary flow
        const steps = resolveEditNavigationSteps(locationPathName, widgetNavigatorStack);
        if (steps > 0) {
            navigationBack(steps);
            return;
        }
        navigationBack();
    };

    const getColors = (color: string): any => {
        switch (color) {
            case 'red':
                return Colors.red;
            case 'orange':
                return Colors.orange;
            default:
                return Colors.blue;
        }
    };

    const getServiceAdminTextColor = (): any => {
        // keeping this commented out for now, as we are not using service admin specific color in the header
        // if (isServiceAdmin) {
        //     return 'text-white';
        // } else
        if (
            (currentOrg?.id || location.pathname === `/${currentRealmName}/myProfile` || isSuperRealm) &&
            !timelineStatusColor &&
            !NO_ORGANIZATION
        ) {
            return 'text-secondary';
        }
        return 'text-white';
    };

    const getIconColor = (): string => {
        if (timelineStatusColor) {
            return '#FFF';
        }
        return extraProps?.iconColor ?? '#424e54';
    };

    const getLeftIcon = (): any =>
        (currentOrg?.id || location.pathname === `/${currentRealmName}/myProfile` || isSuperRealm || isAdminRealm) && (
            <IconButton
                disabled={disableHeaderBackNavigation}
                onClick={widgetId ? redirectionHandler : handleNavigateBack}
            >
                {extraProps?.crossIcon ? (
                    <Close className="black-500" />
                ) : (
                    <ArrowBackIcon
                        data-testid="ArrowBackIcon"
                        style={{
                            color: getIconColor(),
                        }}
                    />
                )}
            </IconButton>
        );

    const getToolbarClassName = (): string => {
        let className = 'padding-x-16 min-height-56 ';
        // keeping this commented out for now, as we are not using service admin specific color in the header
        // if (isServiceAdmin) {
        //     className += 'bg-blue-500';
        // } else
        if (
            (currentOrg?.id || location.pathname === `/${currentRealmName}/myProfile` || isSuperRealm) &&
            !timelineStatusColor &&
            !NO_ORGANIZATION
        ) {
            className += 'bg-white';
        }
        return className;
    };

    const getSubtitle = (): any => resolveSubtitleContent(extraProps, appHeaderSubTitle, currentOrg?.name);

    const getSubtitleJSX = (): JSX.Element => {
        if (extraProps?.noSubtitle) {
            return <></>;
        }
        return (
            // keeping this commented out for now, as we are not using service admin specific color in the header
            // <Typography variant={'body1'} color={timelineStatusColor || isServiceAdmin ? '#fff' : Colors.black[500]}>
            <Typography variant={'body1'} color={timelineStatusColor ? '#fff' : Colors.black[500]}>
                {getSubtitle()}
            </Typography>
        );
    };

    const getNoBreadcrumbsTitle = (): any => {
        if (NO_ORGANIZATION) {
            return 'No Organization';
        } else if (pageHeaderData?.title) {
            return pageHeaderData?.title;
        } else if (appHeaderTitle) {
            return appHeaderTitle;
        }
        return <CustomTransComponent translationKey={extraProps?.title} locale={locale} />;
    };

    const getNoBreadcrumbsHeader = (): any => {
        if (extraProps?.path?.includes('editWidget')) {
            return (
                <>
                    {widgetLoader ? (
                        <Skeleton
                            animation="wave"
                            variant="rounded"
                            width={200}
                            height={15}
                            sx={{ marginBottom: '10px', marginTop: '10px' }}
                        />
                    ) : (
                        pageHeaderData?.title ?? ''
                    )}
                </>
            );
        }
        return getNoBreadcrumbsTitle();
    };

    return (
        <Box sx={getBackgroundSx(theme, location.pathname)} minHeight={'100vh'} position={'relative'}>
            <AppBar
                className="position-sticky"
                sx={getAppBarSx(pageHeaderData, extraProps, timelineStatusColor, headerColorChange, getColors)}
            >
                <Toolbar className={getToolbarClassName()}>
                    {getLeftIcon()}
                    <Stack pl={3} direction={'column'}>
                        {extraProps?.noBreadcrumbs ? (
                            <Typography
                                key={extraProps.path}
                                variant={'h6'}
                                color={'inherit'}
                                className={resolveNoBreadcrumbsClassName(
                                    currentOrg,
                                    location,
                                    currentRealmName,
                                    isSuperRealm
                                )}
                            >
                                {getNoBreadcrumbsHeader()}
                            </Typography>
                        ) : (
                            <Breadcrumbs separator={'/'}>
                                {nestedRoutesList?.map(
                                    (route: Route): any =>
                                        route.title && (
                                            <Typography
                                                key={`${route.path}-breadcrumb`}
                                                variant={'h6'}
                                                color={'inherit'}
                                                onClick={(): void => {
                                                    navigate(`/${currentRealmName}/${route.path}`);
                                                }}
                                                className={getServiceAdminTextColor()}
                                            >
                                                {appHeaderTitle ?? (
                                                    <CustomTransComponent
                                                        translationKey={route.title}
                                                        locale={locale}
                                                    />
                                                )}
                                            </Typography>
                                        )
                                )}
                            </Breadcrumbs>
                        )}

                        <Typography variant={'body1'} color={resolveFontColor(extraProps)}>
                            {t(title)}
                        </Typography>
                        {getSubtitleJSX()}
                    </Stack>
                    <Spacer />
                    {NO_ORGANIZATION && <HeaderActions disabled={resolveHeaderActionsDisabled(currentOrg)} />}
                </Toolbar>
            </AppBar>
            {page}
        </Box>
    );
};
